import React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import { FormConfirmationModal } from '../../components/ConfirmationModal';
import { Field } from '../../components/Form';
import LoadingIndicator from '../../components/LoadingIndicator';
import Tooltip from '../../components/Tooltip';
import { gql } from '../../graphql/generated/gql';
import {
  BillingSummaryStatus,
  PlanTier,
} from '../../graphql/generated/graphql';
import SettingCard from '../../settings/SettingCard';
import { UIActions } from '../../ui/uiSlice';
import useNavigate from '../../utils/router/useNavigate';
import * as styles from './DeleteTeam.module.scss';

const DELETE_ORGANIZATION_MUTATION = gql(/** @complexity 2 */ `
  mutation DeleteOrganization {
    deleteOrganization {
      id
    }
  }
`);

interface DeleteTeamModalProps {
  close: () => void;
}

type Values = {
  delete: string;
};

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const DeleteTeamModal = ({ close }: DeleteTeamModalProps) => {
  const navigate = useNavigate();

  const [deleteOrganization] = useMutation(DELETE_ORGANIZATION_MUTATION);

  const onSubmit = async (values: Values) => {
    if (values.delete === 'delete') {
      try {
        close();
        navigate('/deleted', { state: { isDeleted: true } });
        await deleteOrganization();
      } catch (err) {
        navigate('/admin/import');
      }
    }
  };

  return (
    <FormConfirmationModal<Values>
      title="Delete team"
      description="Deleting a Slab team cannot be undone. All topics, posts, and content will no longer be retrievable."
      form={{
        onSubmit,
        initialValues: { delete: '' },
        validate: (values) => {
          if (values.delete !== 'delete') {
            return { delete: 'Type "delete" to confirm' };
          }
          return {};
        },
      }}
      close={close}
      submitButton={{
        text: 'Delete team',
        danger: true,
      }}
    >
      <div className={styles.info}>
        Type <strong>delete</strong> below to confirm.
      </div>
      <Field type="text" name="delete" placeholder="delete" />
    </FormConfirmationModal>
  );
};

const DELETE_TEAM_QUERY = gql(/** @complexity 4 */ `
  query DeleteTeam {
    billingSummary {
      id
      status
    }
  }
`);

const DeleteTeam = () => {
  const dispatch = useAppDispatch();
  const planTier = useAppSelector(
    (state) => state.session.organization.planTier,
  );

  const { data, loading } = useQuery(DELETE_TEAM_QUERY);

  const canDelete =
    planTier === PlanTier.Free ||
    data?.billingSummary.status !== BillingSummaryStatus.Active;

  const openModal = () => {
    dispatch(UIActions.openModal(DeleteTeamModal));
  };

  return (
    <SettingCard
      title="Delete team"
      description="Permanently delete this Slab team"
      actions={
        loading ? (
          <LoadingIndicator />
        ) : (
          <Tooltip
            content={
              canDelete ? undefined : 'Please cancel your\nsubscription first'
            }
          >
            <Button
              onClick={openModal}
              variant="secondary"
              disabled={!canDelete}
              danger
            >
              Delete team
            </Button>
          </Tooltip>
        )
      }
    />
  );
};

export default DeleteTeam;
